import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';

import { AuthenticationService } from './authentication.service';
import { BaseWorkPackageDto, GrantType, UserGrantsDto } from '@api/backend-connector/models';
import { UsersApi } from '@api/backend-connector/services';

export const anyOf = (grants: string[], requirements: string[]) => {
  const result = !!grants.find((g) => requirements.includes(g));
  return result;
};

export const AppPermissionMap = {
  users: {
    view: [GrantType.ViewUsers] as string[],
    manage: [GrantType.ManageUsers] as string[],
  },
  groups: {
    view: [GrantType.ViewGroups] as string[],
    manage: [GrantType.ManageGroups] as string[],
  },
  roles: {
    view: [GrantType.ViewRoles] as string[],
    manage: [GrantType.ManageRoles] as string[],
  },
  teamPlanners: {
    view: [GrantType.ViewTeamPlanners] as string[],
    manage: [GrantType.ManageTeamPlanners] as string[],
  },
  wpTypes: {
    view: [GrantType.ViewWorkPackageTypes] as string[],
    manage: [GrantType.ManageWorkPackageTypes] as string[],
  },
  wpStatuses: {
    view: [GrantType.ViewWorkWorkPackageStatuses] as string[],
    manage: [GrantType.ManageWorkPackageStatuses] as string[],
  },
  wpPriorities: {
    view: [GrantType.ViewWorkPackagePriorities] as string[],
    manage: [GrantType.ManageWorkPackagePriorities] as string[],
  },
  globalConfigurations: {
    view: [GrantType.ViewGlobalConfigurations] as string[],
    manage: [GrantType.ManageGlobalConfigurations] as string[],
  },
  costTypes: {
    view: [GrantType.ViewCostTypes] as string[],
    manage: [GrantType.ManageCostTypes] as string[],
  },
  laborCosts: {
    view: [GrantType.ViewLaborCosts] as string[],
    manage: [GrantType.ManageLaborCosts] as string[],
  },
  laborActivities: {
    view: [GrantType.ViewLaborActivities] as string[],
    manage: [GrantType.ManageLaborActivities] as string[],
  },
  budgets: {
    view: [GrantType.ViewBudgets] as string[],
    create: [GrantType.CreateBudget] as string[],
    update: [GrantType.UpdateBudgets] as string[],
    delete: [GrantType.UpdateBudgets] as string[],
  },
  workProfiles: {
    view: [GrantType.ViewWorkProfiles] as string[],
  },
  workCalendarDays: {
    view: [GrantType.ViewWorkCalendarDays] as string[],
  },
  workpackages: {
    edit: {
      name: [GrantType.EditWorkPackages, GrantType.EditWorkPackagesInfo] as string[],
      description: [GrantType.EditWorkPackages, GrantType.EditWorkPackagesInfo] as string[],
      dates: [GrantType.EditWorkPackages, GrantType.EditWorkPackagesDates] as string[],
      assignee: [GrantType.EditWorkPackages, GrantType.EditWorkPackagesAssignee] as string[],
      targetGroup: [GrantType.EditWorkPackagesTargetGroup] as string[],
      categories: [GrantType.EditWorkPackages, GrantType.EditWorkPackagesCategories] as string[],
      type: [GrantType.EditWorkPackages, GrantType.EditWorkPackagesType] as string[],
      priority: [GrantType.EditWorkPackages, GrantType.EditWorkPackagesPriority] as string[],
      status: [GrantType.EditWorkPackages, GrantType.EditWorkPackagesStatus] as string[],
      hierarchy: [GrantType.EditWorkPackages, GrantType.EditWorkPackagesHierarchy] as string[],
      dynamicFormsSchema: [GrantType.EditWorkPackages, GrantType.EditWorkPackagesDynamicFormsSchema] as string[],
      dynamicFormsValue: (categoryName: string[]) => [
        ...([GrantType.EditWorkPackages, GrantType.EditWorkPackagesDynamicFormsValue] as string[]),
        ...categoryName.map((c) => `editWorkPackagesDynamicFormsValue-withCategory-${c}`),
      ],
      attachments: [GrantType.EditWorkPackages, GrantType.EditWorkPackagesAttachments] as string[],
    },
    editAssigned: {
      name: [GrantType.EditAssignedWorkPackagesInfo] as string[],
      description: [GrantType.EditAssignedWorkPackagesInfo] as string[],
      dates: [GrantType.EditAssignedWorkPackagesDates] as string[],
      assignee: [GrantType.EditAssignedWorkPackagesAssignee] as string[],
      targetGroup: [GrantType.EditAssignedWorkPackagesTargetGroup] as string[],
      categories: [GrantType.EditAssignedWorkPackagesCategories] as string[],
      type: [GrantType.EditAssignedWorkPackagesType] as string[],
      priority: [GrantType.EditAssignedWorkPackagesPriority] as string[],
      status: [GrantType.EditAssignedWorkPackagesStatus] as string[],
      hierarchy: [GrantType.EditAssignedWorkPackagesHierarchy] as string[],
      dynamicFormsSchema: [GrantType.EditAssignedWorkPackagesDynamicFormsSchema] as string[],
      dynamicFormsValue: [GrantType.EditAssignedWorkPackagesDynamicFormsValue] as string[],
      attachments: [GrantType.EditAssignedWorkPackagesAttachments] as string[],
    },
    inventoryTransactions: {
      edit: [GrantType.EditWorkPackages, GrantType.EditWorkPackagesInventoryTransactions] as string[],
      editAssigned: [GrantType.EditAssignedWorkPackagesInventoryTransactions] as string[],
    },
    create: [GrantType.CreateWorkPackages] as string[],
    createFromTemplate: (categoryName: string[]) => [
      ...([GrantType.CreateWorkPackages, GrantType.CreateWorkPackagesFromTemplate] as string[]),
      ...categoryName.map((c) => `createWorkPackagesFromTemplate-withCategory-${c}`),
    ],
    delete: [GrantType.DeleteWorkPackages] as string[],
    import: [GrantType.CreateWorkPackages] as string[],
    export: [GrantType.ExportProjectWorkPackages] as string[],
  },
  templates: {
    view: [GrantType.ManageTemplates] as string[],
    edit: {
      name: [GrantType.ManageTemplates] as string[],
      description: [GrantType.ManageTemplates] as string[],
      assignee: [GrantType.ManageTemplates] as string[],
      groups: [GrantType.ManageTemplates] as string[],
      categories: [GrantType.ManageTemplates] as string[],
      type: [GrantType.ManageTemplates] as string[],
      priority: [GrantType.ManageTemplates] as string[],
      status: [GrantType.ManageTemplates] as string[],
      hierarchy: [GrantType.ManageTemplates] as string[],
      dynamicFormsSchema: [GrantType.ManageTemplates] as string[],
      dynamicFormsValue: [GrantType.ManageTemplates] as string[],
      attachments: [GrantType.ManageTemplates] as string[],
      customFields: [GrantType.ManageTemplates] as string[],
    },
    create: [GrantType.ManageTemplates] as string[],
    delete: [GrantType.ManageTemplates] as string[],
    import: [GrantType.ManageTemplates] as string[],
    export: [GrantType.ManageTemplates] as string[],
  },
  projects: {
    create: [GrantType.CreateProject] as string[] as string[] as string[],
    edit: {
      name: [GrantType.EditProject, GrantType.EditProjectInfo] as string[],
      description: [GrantType.EditProject, GrantType.EditProjectInfo] as string[],
      dates: [GrantType.EditProject, GrantType.EditProjectDates] as string[],
      isArchived: [GrantType.EditProject, GrantType.EditProjectArchived] as string[],
      isPublic: [GrantType.EditProject, GrantType.EditProjectPublic] as string[],
      status: [GrantType.EditProject, GrantType.EditProjectStatus] as string[],
      projectStatusDescription: [GrantType.EditProject, GrantType.EditProjectStatus] as string[],
      budgetEnabled: [GrantType.EditProject] as string[],
      allowMultipleBudget: [GrantType.EditProject] as string[],
      costFlowEnabled: [GrantType.EditProject] as string[],
      revenueEnabled: [GrantType.EditProject] as string[],
      cashFlowEnabled: [GrantType.EditProject] as string[],
      workPackageEstimates: [GrantType.EditProject] as string[],
      personEstimates: [GrantType.EditProject] as string[],
      allowBudgetCapOvercome: [GrantType.EditProject] as string[],
      allowEstimatesOvercome: [GrantType.EditProject] as string[],
      allowNotAllocatedEffectives: [GrantType.EditProject] as string[],
      allowUpdateBudgetVersionEstimates: [GrantType.EditProject] as string[],
      allowUpdateActiveBudget: [GrantType.EditProject] as string[],
      customFieldsValue: [GrantType.EditProject] as string[],
      costTypes: [GrantType.EditProject] as string[],
      laborCost: [GrantType.EditProject] as string[],
      workPackageTypes: [GrantType.EditProject] as string[],
      data: [GrantType.EditProject] as string[],
    },
    members: {
      user: [GrantType.ManageProjectUsersMemberships] as string[],
      group: [GrantType.ManageProjectGroupsMemberships] as string[],
    },
    categories: {
      edit: [GrantType.EditProjectWorkPackageCategories] as string[],
    },
  },
  laborCost: {
    manageSelf: [GrantType.ManageEffective, GrantType.ManageSelfLaborCost] as string[],
  },
  costs: {
    cashAnalytics: [GrantType.ViewCashAnalytics] as string[],
    costAnalytics: [GrantType.ViewCostAnalytics] as string[],
    manageEffective: [GrantType.ManageEffective] as string[],
    manageEstimates: [GrantType.ManageEstimates] as string[],
  },
  inventoryItems: {
    view: [GrantType.ViewInventoryItems] as string[],
    manage: [GrantType.ManageInventoryItems] as string[],
  },
} as const;

export const mergeWorkPackageGrants = (
  grants: string[],
  assigneeGrants: string[],
  context: {
    isAssignee: boolean;
    isPlatformAdmin: boolean;
    isWorkPackageReadonly: boolean;
  },
) => {
  const candidateGrants = [...grants, ...(context.isAssignee ? assigneeGrants : [])];
  if (context.isPlatformAdmin) {
    return candidateGrants;
  }
  if (context.isWorkPackageReadonly) {
    return [];
  }
  return candidateGrants;
};

export const mergeWorkPackageGrantsContext = (
  me: string,
  isPlatformAdmin: boolean,
  workPackage: BaseWorkPackageDto,
) => {
  return {
    isAssignee: workPackage.assignees.map((a) => a.id).includes(me),
    isPlatformAdmin: isPlatformAdmin,
    isWorkPackageReadonly: workPackage.isWorkPackageReadonly,
    categorieNames: workPackage.workPackageCategories.map((c) => c.name),
  };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
const fake = [
  GrantType.ViewUsers,
  GrantType.ManageUsers,
  GrantType.ViewGroups,
  // GrantType.ManageGroups,
  GrantType.ManageGroups,
  GrantType.ViewRoles,
  GrantType.ManageRoles,
  GrantType.ViewTeamPlanners,
  GrantType.ManageTeamPlanners,
  GrantType.ViewWorkPackageTypes,
  GrantType.ManageWorkPackageTypes,
  GrantType.ViewWorkWorkPackageStatuses,
  GrantType.ManageWorkPackageStatuses,
  GrantType.ViewWorkPackagePriorities,
  // GrantType.ManageWorkPackagePriorities,
  GrantType.ViewCostTypes,
  GrantType.ManageCostTypes,
  GrantType.ViewLaborActivities,
  GrantType.ManageLaborActivities,
  GrantType.ViewBudgets,
  GrantType.CreateBudget,
  GrantType.UpdateBudgets,

  GrantType.CreateProject,
  GrantType.CreateWorkPackages,

  // GrantType.EditProject,

  // GrantType.ManageSelfLaborCost,
  GrantType.ManageEffective,
  GrantType.EditWorkPackagesHierarchy,
  GrantType.EditWorkPackagesDates,
];

const mapUserGrantDto = (userGrantDto: UserGrantsDto): string[] => {
  return userGrantDto.isPlatformAdmin
    ? Array.from(new Set([...Object.values(GrantType), ...userGrantDto.existingCustomFieldsGrants]))
    : Array.from(new Set([...userGrantDto.projectGrants, ...userGrantDto.projectCustomFieldsGrants]));

  // return userGrantDto.isPlatformAdmin ? fake : fake;
};

const mapGlobalGrantDto = (userGrantDto: UserGrantsDto): string[] => {
  return userGrantDto.isPlatformAdmin
    ? Array.from(new Set([...Object.values(GrantType), ...userGrantDto.existingCustomFieldsGrants]))
    : Array.from(new Set([...userGrantDto.globalGrants, ...userGrantDto.globalCustomFieldsGrants]));

  // return userGrantDto.isPlatformAdmin ? fake : fake;
};

const mapUserDistinctGrantDto = (userGrantDto: UserGrantsDto): string[] => {
  return userGrantDto.isPlatformAdmin
    ? Array.from(new Set([...Object.values(GrantType), ...userGrantDto.existingCustomFieldsGrants]))
    : Array.from(new Set([...userGrantDto.distinctGrants, ...userGrantDto.distinctCustomFieldsGrants]));

  // return userGrantDto.isPlatformAdmin ? fake : fake;
};

@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private readonly usersApi = inject(UsersApi);
  private readonly authenticationService = inject(AuthenticationService);

  private readonly _permissionScope$ = new BehaviorSubject<string | undefined>(undefined);
  private readonly _refreshGlobalGrants$ = new BehaviorSubject<void>(undefined);

  private readonly grantsInScope$ = this._permissionScope$.pipe(
    distinctUntilChanged(),
    // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
    switchMap((projectId) => {
      return this.globalGrants$.pipe(
        switchMap(({ globalGrants }) => {
          return this.usersApi.getUserGrants({ projectId }).pipe(
            map(mapUserGrantDto),
            map((scopedGrants) => ({
              scope: projectId,
              grants: Array.from(new Set([...globalGrants, ...scopedGrants])),
            })),
            // return of(undefined).pipe(
            // if isPlatformAdmin return all roles
            // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
            // map((grants) => {
            //   return {
            //     scope: projectId,
            //     grants: [
            //       GrantType.ManageGroups,
            //       GrantType.EditProject,
            //       GrantType.CreateWorkPackages,
            //       GrantType.EditWorkPackages,
            //       GrantType.EditWorkPackagesDates,
            //     ],
            //   };
            // }),
          );
        }),
      );
    }),
    tap((val) => console.warn(val)),
    shareReplay({ refCount: false, bufferSize: 1 }),
  );

  private readonly globalGrants$ = this._refreshGlobalGrants$.pipe(
    // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
    switchMap(() => {
      return this.usersApi.getUserGrants().pipe(
        map((dto) => ({
          grants: mapUserGrantDto(dto),
          globalGrants: mapGlobalGrantDto(dto),
          distinctGrants: mapUserDistinctGrantDto(dto),
          isPlatformAdmin: dto.isPlatformAdmin,
          isSuperAdmin: dto.isSuperAdmin,
        })),
        // global
        // return of(undefined).pipe(
        // if isPlatformAdmin return all roles
        // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
        // map((grants) => {
        //   return [
        //     // GrantType.ManageGroups,
        //     GrantType.EditProject,
        //     GrantType.CreateWorkPackages,
        //     GrantType.EditWorkPackages,
        //     GrantType.EditWorkPackagesDates,
        //   ];
        // }),
      );
    }),
    tap((val) => console.warn(val)),
    shareReplay({ refCount: false, bufferSize: 1 }),
  );

  private readonly distinctGrants$ = this.globalGrants$.pipe(map(({ distinctGrants }) => distinctGrants));

  getGrantsInScope(projectId: string) {
    if (this._permissionScope$.value !== projectId) {
      this._permissionScope$.next(projectId);
      this._refreshGlobalGrants$.next();
    }
    return this.grantsInScope$.pipe(
      filter((grantsInScope) => grantsInScope.scope === projectId),
      map((grantsInScope) => grantsInScope.grants),
    );
  }

  getGlobalGrants() {
    return this.globalGrants$.pipe(map((global) => global.globalGrants));
  }

  getDistinctGrants() {
    return this.globalGrants$.pipe(map((global) => global.grants));
  }

  isPlatformAdmin() {
    return this.globalGrants$.pipe(map((global) => global.isPlatformAdmin));
  }

  isSuperAdmin() {
    return this.globalGrants$.pipe(map((global) => global.isSuperAdmin));
  }

  canCreateProjects() {
    return this.globalGrants$.pipe(map((global) => anyOf(global.grants, AppPermissionMap.projects.create)));
  }

  // globals permissions

  canManageUsers() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.users.manage)));
  }

  canViewUsers() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.users.view)));
  }

  canDisableUsers() {
    return this.isPlatformAdmin();
  }

  canViewGroups() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.groups.view)));
  }

  canManageGroups() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.groups.manage)));
  }

  canViewRoles() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.roles.view)));
  }

  canManageRoles() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.roles.manage)));
  }

  canViewTeamPlanners() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.teamPlanners.view)));
  }

  canManageTeamPlanners() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.teamPlanners.manage)));
  }

  canViewWpTypes() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.wpTypes.view)));
  }

  canManageWpTypes() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.wpTypes.manage)));
  }

  canViewWpStatuses() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.wpStatuses.view)));
  }

  canManageWpStatuses() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.wpStatuses.manage)));
  }

  canViewWpPriorities() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.wpPriorities.view)));
  }

  canManageWpPriorities() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.wpPriorities.manage)));
  }

  canManageGlobalConfigurations() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.teamPlanners.manage)));
  }

  canViewCostTypes() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.costTypes.view)));
  }

  canManageCostTypes() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.costTypes.manage)));
  }

  canManageTenants() {
    return this.isSuperAdmin();
  }

  canViewLaborCosts() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.laborCosts.view)));
  }

  canManageLaborCosts() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.laborCosts.manage)));
  }

  canViewLaborActivities() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.laborActivities.view)));
  }

  canManageLaborActivities() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.laborActivities.manage)));
  }

  canViewBudgets() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.budgets.view)));
  }

  canCreateBudget() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.budgets.create)));
  }

  canActivateBudget() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.budgets.update)));
  }

  canUpdateBudget() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.budgets.update)));
  }

  canDeleteBudget() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.budgets.delete)));
  }

  canViewWorkProfiles() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.workProfiles.view)));
  }

  canViewCalendarDays() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.workCalendarDays.view)));
  }

  canViewGlobalConfigurations() {
    return this.getGlobalGrants().pipe(map((grants) => anyOf(grants, AppPermissionMap.globalConfigurations.view)));
  }

  // tabs

  canViewCalendar() {
    this;
    return of(true);
  }

  canViewTeamAllocation() {
    this;
    return of(true);
  }

  canViewAdminSettings() {
    return this.getGlobalGrants().pipe(
      map((grants) =>
        anyOf(grants, [
          ...AppPermissionMap.users.view,
          ...AppPermissionMap.users.manage,
          ...AppPermissionMap.groups.view,
          ...AppPermissionMap.groups.manage,
          ...AppPermissionMap.roles.view,
          ...AppPermissionMap.roles.manage,
          ...AppPermissionMap.teamPlanners.view,
          ...AppPermissionMap.teamPlanners.manage,
          ...AppPermissionMap.wpTypes.view,
          ...AppPermissionMap.wpTypes.manage,
          ...AppPermissionMap.wpStatuses.view,
          ...AppPermissionMap.wpStatuses.manage,
          ...AppPermissionMap.wpPriorities.view,
          ...AppPermissionMap.wpPriorities.manage,
          ...AppPermissionMap.globalConfigurations.view,
          ...AppPermissionMap.globalConfigurations.manage,
          ...AppPermissionMap.costTypes.view,
          ...AppPermissionMap.costTypes.manage,
          ...AppPermissionMap.laborCosts.view,
          ...AppPermissionMap.laborCosts.manage,
          ...AppPermissionMap.laborActivities.view,
          ...AppPermissionMap.laborActivities.manage,
          ...AppPermissionMap.budgets.view,
          ...AppPermissionMap.budgets.create,
          ...AppPermissionMap.budgets.update,
          ...AppPermissionMap.budgets.delete,
          ...AppPermissionMap.workProfiles.view,
          ...AppPermissionMap.workCalendarDays.view,
        ]),
      ),
    );
  }

  // in project scope permissions

  canArchiveProject(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(
      map((grants) => anyOf(grants, AppPermissionMap.projects.edit.isArchived)),
    );
  }

  canEditProject(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(
      map((grants) =>
        anyOf(
          grants,
          Object.values(AppPermissionMap.projects.edit).flatMap((g) => g),
        ),
      ),
    );
  }

  canCreateWorkPackagesFromTemplate(projectId: string, categoryNames: string[]) {
    return this.getGrantsInScope(projectId).pipe(
      map((grants) => anyOf(grants, AppPermissionMap.workpackages.createFromTemplate(categoryNames))),
    );
  }

  canCreateWorkPackages(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(map((grants) => anyOf(grants, AppPermissionMap.workpackages.create)));
  }

  canDeleteWorkPackages(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(map((grants) => anyOf(grants, AppPermissionMap.workpackages.delete)));
  }

  canImportWorkPackages(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(map((grants) => anyOf(grants, AppPermissionMap.workpackages.import)));
  }

  canExportWorkPackages(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(map((grants) => anyOf(grants, AppPermissionMap.workpackages.export)));
  }

  canEditWorkPackageHierarchy(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(
      map((grants) => anyOf(grants, AppPermissionMap.workpackages.edit.hierarchy)),
    );
  }

  canEditWorkPackageDates(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(
      map((grants) => anyOf(grants, AppPermissionMap.workpackages.edit.dates)),
    );
  }

  canManageSelfLaborCosts(projectId?: string) {
    return projectId
      ? this.getGrantsInScope(projectId).pipe(map((grants) => anyOf(grants, AppPermissionMap.laborCost.manageSelf)))
      : this.distinctGrants$.pipe(map((grants) => anyOf(grants, AppPermissionMap.laborCost.manageSelf)));
  }

  canManageEstimates(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(
      map((grants) => anyOf(grants, AppPermissionMap.costs.manageEstimates)),
    );
  }

  canManageEffective(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(
      map((grants) => anyOf(grants, AppPermissionMap.costs.manageEffective)),
    );
  }

  canViewCostAnalysis2(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(map((grants) => anyOf(grants, AppPermissionMap.costs.costAnalytics)));
  }

  canViewCashAnalysis(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(map((grants) => anyOf(grants, AppPermissionMap.costs.cashAnalytics)));
  }

  canManageCollaborators(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(
      map((grants) => anyOf(grants, AppPermissionMap.projects.members.user)),
    );
  }

  canViewTemplates(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(map((grants) => anyOf(grants, AppPermissionMap.templates.view)));
  }

  canCreateTemplates(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(map((grants) => anyOf(grants, AppPermissionMap.templates.create)));
  }

  canDeleteTemplates(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(map((grants) => anyOf(grants, AppPermissionMap.templates.delete)));
  }

  canImportTemplates(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(map((grants) => anyOf(grants, AppPermissionMap.templates.import)));
  }

  canExportTemplates(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(map((grants) => anyOf(grants, AppPermissionMap.templates.export)));
  }

  canEditWorkPackageDynamicFormsSchema(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(
      map((grants) => anyOf(grants, AppPermissionMap.workpackages.edit.dynamicFormsSchema)),
    );
  }

  canEditTemplateDynamicFormsSchema(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(
      map((grants) => anyOf(grants, AppPermissionMap.templates.edit.dynamicFormsSchema)),
    );
  }

  canEditWorkPackageInventoryTransactions(workPackage: BaseWorkPackageDto) {
    return combineLatest([this.getGrantsInScope(workPackage.projectId), this.authenticationService.getMe()]).pipe(
      map(([grants, { id, isPlatformAdmin }]) => {
        const mergeGrantsContext = mergeWorkPackageGrantsContext(id, isPlatformAdmin, workPackage);
        return anyOf(
          grants,
          mergeWorkPackageGrants(
            AppPermissionMap.workpackages.inventoryTransactions.edit,
            AppPermissionMap.workpackages.inventoryTransactions.editAssigned,
            mergeGrantsContext,
          ),
        );
      }),
    );
  }

  canViewBudgetsTab(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(
      map((grants) =>
        anyOf(
          grants,
          [...Object.values(AppPermissionMap.costs), ...Object.values(AppPermissionMap.costs)].flatMap((g) => g),
        ),
      ),
    );
  }

  canViewCollaboratorsTab(projectId: string) {
    return this.getGrantsInScope(projectId).pipe(
      map((grants) =>
        anyOf(
          grants,
          Object.values(AppPermissionMap.projects.members).flatMap((g) => g),
        ),
      ),
    );
  }
}
